import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";

export type PasswordJSON = z.infer<typeof PasswordSchema.PasswordJSONZ>;
export type PasswordParams = z.infer<typeof PasswordSchema.PasswordZ>;
export type PasswordIssues = {
  password: string[];
  confirmPassword: string | null;
};

export default class PasswordSchema {
  public static readonly PasswordJSONZ = z.string();

  public static readonly PasswordZ = z
    .object({
      password: z.string(),
      confirmPassword: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const { password, confirmPassword } = data;

      let issues: PasswordIssues = {
        password: [],
        confirmPassword: null,
      };

      // If password is null or empty string, skip other validation and return invalid
      if (password == null || password.length < 1) {
        issues.password.push("Password required");
      } else {
        issues = PasswordSchema.validatePasswordRules(password);
      }

      // Only check confirmPassword if it isn't null/undefined
      if (confirmPassword !== null && confirmPassword !== undefined) {
        if (confirmPassword.length < 1) {
          issues.confirmPassword = "Confirm Password required";
        } else if (password !== confirmPassword) {
          issues.confirmPassword = "Password and Confirm Password must match";
        }
      }

      // Convert our collected issues into Zod issues
      for (const msg of issues.password) {
        ctx.addIssue({
          code: "custom",
          path: ["password"],
          message: msg,
        });
      }

      if (issues.confirmPassword !== null) {
        ctx.addIssue({
          code: "custom",
          path: ["confirmPassword"],
          message: issues.confirmPassword,
        });
      }
    });

  public static validate(values: unknown, logError: boolean = true): PasswordParams {
    return parseOrThrow(PasswordSchema.PasswordZ, values, {
      logError,
      schemaName: "PasswordSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): PasswordJSON {
    return parseOrThrow(PasswordSchema.PasswordJSONZ, values, {
      logError: true,
      schemaName: "PasswordSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is PasswordParams {
    try {
      PasswordSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is PasswordJSON {
    try {
      PasswordSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PasswordJSON): PasswordParams {
    if (isNullOrUndefined(data)) return data;
    const parsed = parseOrThrow(PasswordSchema.PasswordJSONZ, data, {
      logError: true,
      schemaName: "PasswordSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return PasswordSchema.codec.decode(parsed);
  }

  public static serialize(Password: PasswordParams): PasswordJSON {
    if (isNullOrUndefined(Password)) return Password;
    const parsed = PasswordSchema.validate(Password);
    if (isNullOrUndefined(parsed)) return parsed;
    return PasswordSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(PasswordSchema.PasswordJSONZ, PasswordSchema.PasswordZ, {
    decode: (data) => ({
      password: data,
    }),
    encode: (data) => data.password,
  });

  private static validatePasswordRules(password: string): PasswordIssues {
    const issues: PasswordIssues = {
      password: [],
      confirmPassword: null,
    };

    const regexTotalChar = /^.{8,}$/;
    if (!regexTotalChar.test(password)) {
      issues.password.push("Password must contain at least 8 characters");
    }

    const regexUpper = /(?=.*?[A-Z])/;
    if (!regexUpper.test(password)) {
      issues.password.push("Password must contain at least 1 uppercase letter");
    }

    const regexLower = /(?=.*?[a-z])/;
    if (!regexLower.test(password)) {
      issues.password.push("Password must contain at least 1 lowercase letter");
    }

    const regexNum = /(?=.*?\d)/;
    if (!regexNum.test(password)) {
      issues.password.push("Password must contain at least 1 number");
    }

    const regexSymbol = /(?=.*?[#?!@$%^&*:;'"/.,<>()+=\\|\][}{-])/;
    if (!regexSymbol.test(password)) {
      issues.password.push("Password must contain at least 1 symbol (#?!@$%^&*-)");
    }

    return issues;
  }
}
